package com.advaita.cloud.drive.shared

import kotlinx.serialization.Serializable

/** Page of file/folder metadata from the recursive listing. */
@Serializable
data class FileTreeResponse(
    val items: List<FileDto>,
    val nextPageToken: String? = null,
)

/**
 * ADV-1329: one page of `GET /api/shared`, the roots shared with the caller. [truncated] is true
 * when the vault's list was longer than the page cap the server walks, so the list is incomplete.
 *
 * ADV-1330 D5 (S7), with the share UI on: [shares] maps an item's id to the sharer, the level and
 * the grant id. [sharesStatus] is not [SourceStatus.OK] when the vault did not give them, and then
 * the UI shows a banner over a list that is still complete: `unavailable` (the vault did not
 * answer), `rejected` (the vault refused the read), or `refused` (the session's org is not from
 * the token's claim, so DataHub did not ask). [peopleStatus] is az2's, for the
 * sharers' names. With the share UI off, [shares] is empty and both statuses are null.
 */
@Serializable
data class SharedPageResponse(
    val items: List<FileDto>,
    val nextPageToken: String? = null,
    val truncated: Boolean = false,
    val shares: Map<String, SharedGrantInfoDto> = emptyMap(),
    val sharesStatus: String? = null,
    val peopleStatus: String? = null,
)

/** A presigned download for one file: the URL plus any headers the caller MUST replay. */
@Serializable
data class DownloadUrlResponse(
    val url: String,
    val method: String = "GET",
    val headers: Map<String, String> = emptyMap(),
    val expiresAt: String,
)

/** One file's metadata with its presigned download attached (batch endpoint). Files only. */
@Serializable
data class FileDownloadDto(
    val id: String,
    val displayName: String,
    val logicalPath: String,
    val sizeBytes: Long? = null,
    val mimeType: String? = null,
    val status: Status,
    val download: DownloadUrlResponse,
)

/** Page of presigned downloads for every file in a folder subtree. */
@Serializable
data class BatchDownloadResponse(
    val items: List<FileDownloadDto>,
    val nextPageToken: String? = null,
)
